import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useDebounce } from "../../hooks/useDebounce";
import {
  buildDisplayNameFromUser,
  buildDisplayNameFromMaybeObject,
  resolveDisplayName,
  isLikelyUsername,
} from "../../lib/displayName";
import { 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Clock, 
  User, 
  Building,
  Calendar,
  CheckCircle,
  AlertCircle,
  XCircle,
  Eye,
  Edit,
  BarChart3,
  TrendingUp,
  Activity,
  Trash2,
  Download,
  FileSpreadsheet
} from "lucide-react";
import OrdenForm from "./OrdenForm";
import OrdenDetalleModal from "./OrdenDetalleModal";
import SmartAdaptiveList from "../ui/SmartAdaptiveList";
import SmartModal from "../ui/SmartModal";

export default function OrdenesList({ agendamientoParaOrden, onAgendamientoUsado }) {
  const { user } = useAuth();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [ordenDetalle, setOrdenDetalle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filtros, setFiltros] = useState({
    fechaDesde: "",
    fechaHasta: "",
    tecnicoId: "",
    sucursal: "",
    clienteId: "",
    estado: "",
    tipoServicio: ""
  });
  
  const [tecnicos, setTecnicos] = useState([]);

  // Helper para formatear fechas correctamente
  const formatearFechaLista = (fechaStr) => {
    if (!fechaStr) return 'Sin fecha';
    try {
      // Extraer solo la parte de fecha (YYYY-MM-DD)
      let fecha = fechaStr;
      if (fecha.includes('T')) {
        fecha = fecha.split('T')[0];
      }
      // Agregar hora del mediodía para evitar problemas de zona horaria
      return new Date(fecha + 'T12:00:00');
    } catch {
      return new Date(fechaStr);
    }
  };

  const formatMinutesToHHMM = (minutes) => {
    if (!minutes || minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  
  const [clientes, setClientes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  
  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    completadas: 0,
    enProceso: 0,
    pendientes: 0,
    insitu: 0,
    remoto: 0
  });

  const tecnicosPorId = useMemo(() => {
    const map = new Map();
    tecnicos.forEach((tecnico) => {
      const id = tecnico?._id || tecnico?.id;
      if (id) {
        map.set(String(id), tecnico);
      }
    });
    return map;
  }, [tecnicos]);

  const tecnicosPorUsername = useMemo(() => {
    const map = new Map();
    tecnicos.forEach((tecnico) => {
      if (tecnico?.username) {
        map.set(tecnico.username.toLowerCase(), tecnico);
      }
    });
    return map;
  }, [tecnicos]);

  const obtenerNombreTecnico = (orden) => {
    if (!orden) return "";

    // PRIORIDAD 1: Lookup por ID si existe tecnico_id
    const tecnicoId = orden.tecnico_id ? String(orden.tecnico_id) : "";
    if (tecnicoId && tecnicosPorId.has(tecnicoId)) {
      const nombreCompleto = buildDisplayNameFromUser(tecnicosPorId.get(tecnicoId));
      if (nombreCompleto) return nombreCompleto;
    }

    // PRIORIDAD 2: Verificar si algún valor almacenado es username y hacer lookup
    const candidateValues = [
      buildDisplayNameFromMaybeObject(orden.nombre_tecnico),
      buildDisplayNameFromMaybeObject(orden.tecnico_nombre),
      buildDisplayNameFromMaybeObject(orden.tecnico),
    ].filter(Boolean);

    const usernameCandidate = candidateValues.find((value) =>
      value && isLikelyUsername(value)
    );
    if (usernameCandidate) {
      const match = tecnicosPorUsername.get(usernameCandidate.toLowerCase());
      if (match) {
        const nombreCompleto = buildDisplayNameFromUser(match);
        if (nombreCompleto) return nombreCompleto;
      }
    }

    // PRIORIDAD 3: Usar valores almacenados si no son usernames
    const resolved = resolveDisplayName({
      primary: orden.nombre_tecnico,
      secondary: [
        orden.tecnico_nombre,
        orden.tecnico,
        orden.tecnico_detalle,
        orden.usuario_creador_nombre,
        orden.usuario_creador,
      ],
      lookup: () => {
        if (
          tecnicoId &&
          user &&
          (tecnicoId === String(user?._id) || tecnicoId === String(user?.id))
        ) {
          return buildDisplayNameFromUser(user);
        }
        return "";
      },
    });

    if (resolved) {
      return resolved;
    }

    // PRIORIDAD 4: Fallback al usuario actual si coincide el ID
    if (
      orden.tecnico_id &&
      user &&
      (String(orden.tecnico_id) === String(user?._id) ||
        String(orden.tecnico_id) === String(user?.id))
    ) {
      return buildDisplayNameFromUser(user);
    }

    return "";
  };

  // Búsqueda con debounce
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const initialLoadDone = useRef(false);

  // Carga inicial: paralela para datos auxiliares + ordenes
  useEffect(() => {
    if (!user) return;
    const cargarTodo = async () => {
      const promises = [cargarOrdenes()];
      if (user?.rol === "admin") promises.push(cargarTecnicos());
      promises.push(cargarClientes());
      promises.push(cargarSucursales());
      await Promise.allSettled(promises);
      initialLoadDone.current = true;
    };
    cargarTodo();
  }, [user]);

  // Efecto para búsqueda con debounce (solo después de carga inicial)
  useEffect(() => {
    if (initialLoadDone.current) {
      cargarOrdenes();
    }
  }, [debouncedSearchTerm]);

  // Efecto para abrir automáticamente el formulario con datos de agendamiento
  useEffect(() => {
    if (agendamientoParaOrden && initialLoadDone.current) {
      setEditando(null);
      setMostrarForm(true);
    }
  }, [agendamientoParaOrden]);

  // También verificar después de carga inicial
  useEffect(() => {
    if (initialLoadDone.current && agendamientoParaOrden) {
      setEditando(null);
      setMostrarForm(true);
    }
  }, [loading]);

  // Efecto para manejar cambios en filtros (solo después de carga inicial)
  useEffect(() => {
    if (initialLoadDone.current) {
      cargarOrdenes();
    }
  }, [filtros.fechaDesde, filtros.fechaHasta, filtros.tecnicoId, filtros.sucursal, filtros.clienteId, filtros.estado, filtros.tipoServicio]);

  const validarFechas = () => {
    if (filtros.fechaDesde && filtros.fechaHasta) {
      const desde = new Date(filtros.fechaDesde);
      const hasta = new Date(filtros.fechaHasta);
      if (desde > hasta) {
        alert("La fecha 'Desde' no puede ser posterior a la fecha 'Hasta'");
        return false;
      }
    }
    return true;
  };

  async function cargarTecnicos() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/usuarios", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          activo: true
        }
      });
      // Incluir técnicos y admins (ambos pueden estar asignados a órdenes)
      const tecnicosOrdenados = (res.data || [])
        .filter(user => {
          const rol = (user.rol || '').toLowerCase();
          return rol === "tecnico" || rol === "admin";
        })
        .sort((a, b) => {
          const nombreA = buildDisplayNameFromUser(a) || a.username || "";
          const nombreB = buildDisplayNameFromUser(b) || b.username || "";
          return nombreA.localeCompare(nombreB);
        });
      setTecnicos(tecnicosOrdenados);
    } catch (error) {
      console.error("Error cargando técnicos:", error);
    }
  }

  async function cargarClientes() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientesOrdenados = (res.data || []).sort((a, b) => 
        (a.nombre || "").localeCompare(b.nombre || "")
      );
      setClientes(clientesOrdenados);
    } catch (error) {
      console.error("Error cargando clientes:", error);
    }
  }

  async function cargarSucursales() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/sucursales", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSucursales(res.data || []);
    } catch (error) {
      console.error("Error cargando sucursales:", error);
    }
  }

  async function cargarOrdenes() {
    try {
      if (!validarFechas()) {
        return;
      }

      setLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();

      // Formatear las fechas correctamente
      if (filtros.fechaDesde) {
        params.append("fecha_desde", `${filtros.fechaDesde}T00:00:00+00:00`);
      }
      if (filtros.fechaHasta) {
        params.append("fecha_hasta", `${filtros.fechaHasta}T23:59:59+00:00`);
      }
      
      if (filtros.tecnicoId) params.append("tecnico_id", filtros.tecnicoId);
      if (filtros.sucursal) params.append("sucursal", filtros.sucursal);
      if (filtros.clienteId) params.append("cliente_id", filtros.clienteId);
      if (filtros.estado) params.append("estado", filtros.estado);
      if (filtros.tipoServicio) params.append("tipo_servicio", filtros.tipoServicio);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

      const res = await http.get(`/ordenes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const ordenesData = res.data || [];
      setOrdenes(ordenesData);
      
      // Calcular estadísticas
      calcularEstadisticas(ordenesData);
    } catch (error) {
      console.error("Error cargando órdenes:", error);
    } finally {
      setLoading(false);
    }
  }

  function calcularEstadisticas(ordenesData) {
    const total = ordenesData.length;
    const completadas = ordenesData.filter(o => o.estado === 'completada').length;
    const borradores = ordenesData.filter(o => o.estado === 'borrador' || !o.estado).length;
    
    const remoto = ordenesData.filter(o => o.tipo_servicio === 'remoto').length;
    const insitu = ordenesData.filter(o => o.tipo_servicio !== 'remoto').length;
    
    setStats({
      total,
      completadas,
      borradores,
      enProceso: 0, // Ya no se usa
      pendientes: 0, // Ya no se usa
      insitu,
      remoto
    });
  }

  // Función para filtrar y buscar órdenes localmente
  const getFilteredOrdenes = () => {
    if (!debouncedSearchTerm) return ordenes;
    const term = debouncedSearchTerm.toLowerCase();
    return ordenes.filter(orden => {
      const nombreTecnico = obtenerNombreTecnico(orden);
      const estadoTexto = orden.estado === 'completada' ? 'completada' :
        orden.estado === 'borrador' ? 'borrador' : 'sin estado';
      const tipoTexto = orden.tipo_servicio === 'mantenimiento_preventivo' ? 'preventivo' :
        orden.tipo_servicio === 'correctivo' ? 'correctivo' : (orden.tipo_servicio || '');
      return [
        orden._id,
        orden.numero_orden,
        orden.nombre_cliente,
        orden.cliente_nombre,
        nombreTecnico,
        orden.sucursal,
        orden.trabajos_realizados,
        orden.observaciones,
        orden.estado,
        estadoTexto,
        orden.tipo_servicio,
        tipoTexto,
      ].some(field => field && field.toString().toLowerCase().includes(term));
    });
  };

  async function eliminarOrden(id) {
    if (window.confirm("¿Estás seguro de que deseas eliminar esta orden?")) {
      try {
        const token = localStorage.getItem("token");
        await http.delete(`/ordenes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        cargarOrdenes();
      } catch (error) {
        console.error("Error eliminando orden:", error);
        alert("Error al eliminar la orden");
      }
    }
  }

  // Función para abrir edición con datos completos (incluyendo firma)
  async function handleEditarOrden(orden) {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get(`/ordenes/${orden._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Combinar datos del listado con detalle completo (firmas, etc.)
      setEditando({ ...orden, ...res.data });
      setMostrarForm(true);
    } catch (err) {
      console.error("Error cargando detalle para edición:", err);
      // Fallback: abrir con datos incompletos
      setEditando(orden);
      setMostrarForm(true);
    }
  }

  // Función para exportar órdenes
  const exportarOrdenes = async (formato) => {
    try {
      const ordenesIds = filteredOrdenes.map(o => o._id);
      
      if (ordenesIds.length === 0) {
        alert('No hay órdenes para exportar');
        return;
      }

      const token = localStorage.getItem("token");
      const response = await http.post('/ordenes/exportar', {
        formato: formato,
        ordenes_ids: ordenesIds
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Crear URL del blob y descargar
      const blob = new Blob([response.data], {
        type: formato === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0,10);
      link.download = `ordenes_servicio_${timestamp}.${formato === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exportando órdenes:', error);
      alert('Error al exportar las órdenes');
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      fechaDesde: "",
      fechaHasta: "",
      tecnicoId: "",
      sucursal: "",
      clienteId: "",
      estado: "",
      tipoServicio: ""
    });
    setSearchTerm("");
    cargarOrdenes();
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      'completada': 'bg-green-100 text-green-800',
      'borrador': 'bg-yellow-100 text-yellow-800'
    };
    
    const estadoNormalizado = estado || 'borrador';
    return badges[estadoNormalizado] || badges['borrador'];
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      'completada': 'Completada',
      'borrador': 'Borrador'
    };
    
    const estadoNormalizado = estado || 'borrador';
    return textos[estadoNormalizado] || 'Borrador';
  };

  const getTipoServicioBadge = (orden) => {
    const esPreventivo = orden.tipo_servicio === 'mantenimiento_preventivo' || orden.es_mantenimiento_preventivo;
    return esPreventivo ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800';
  };

  const getTipoServicioTexto = (orden) => {
    if (orden.tipo_servicio) {
      return orden.tipo_servicio === 'mantenimiento_preventivo' ? 'Preventivo' :
             orden.tipo_servicio === 'correctivo' ? 'Correctivo' :
             orden.tipo_servicio.charAt(0).toUpperCase() + orden.tipo_servicio.slice(1);
    }
    return orden.es_mantenimiento_preventivo ? 'Preventivo' : 'Correctivo';
  };

  const filteredOrdenes = getFilteredOrdenes();

  // Agrupar órdenes filtradas por mes (más reciente primero)
  const groupedByMonth = (() => {
    const groups = {};
    filteredOrdenes.forEach(orden => {
      const fechaStr = orden.fecha || orden.fecha_programada || orden.created_at;
      let date;
      try {
        let f = fechaStr;
        if (f && f.includes('T')) f = f.split('T')[0];
        date = f ? new Date(f + 'T12:00:00') : new Date();
      } catch {
        date = new Date();
      }
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = { key, label, ordenes: [] };
      groups[key].ordenes.push(orden);
    });
    return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
  })();

  // Cantidad de filtros activos (sin contar el buscador)
  const activeFilterCount = Object.values(filtros).filter(v => v !== '').length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-7 w-7 text-emerald-600" />
              Órdenes de Servicio
            </h1>
            <p className="text-gray-600 mt-1">Gestiona y supervisa todas las órdenes de servicio</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Botones de exportación */}
            <div className="flex gap-2">
              <button
                onClick={() => exportarOrdenes('pdf')}
                disabled={filteredOrdenes.length === 0}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm touch-target disabled:bg-gray-400 disabled:cursor-not-allowed"
                title="Exportar a PDF"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">PDF</span>
              </button>
              <button
                onClick={() => exportarOrdenes('excel')}
                disabled={filteredOrdenes.length === 0}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm touch-target disabled:bg-gray-400 disabled:cursor-not-allowed"
                title="Exportar a Excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span className="hidden sm:inline">Excel</span>
              </button>
            </div>
            {/* Botón Nueva Orden */}
            {(user?.rol === "admin" || user?.rol === "tecnico") && (
              <button
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm touch-target w-full sm:w-auto"
                onClick={() => {
                  setEditando(null);
                  setMostrarForm(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Nueva Orden
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Estadísticas Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Órdenes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completadas</p>
              <p className="text-2xl font-bold text-green-600">{stats.completadas}</p>
            </div>
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Borradores</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.borradores}</p>
            </div>
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Servicios Remotos</p>
              <p className="text-2xl font-bold text-purple-600">{stats.remoto}</p>
            </div>
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Activity className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Servicios In-situ</p>
              <p className="text-2xl font-bold text-orange-600">{stats.insitu}</p>
            </div>
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por cliente, técnico, sucursal..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            {/* Botón de filtros */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors touch-target ${
                showFilters || activeFilterCount > 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-emerald-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Panel de filtros expandible */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 mb-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Fechas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desde
                </label>
                <input
                  type="date"
                  value={filtros.fechaDesde}
                  onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasta
                </label>
                <input
                  type="date"
                  value={filtros.fechaHasta}
                  onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={filtros.estado}
                  onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todos los estados</option>
                  <option value="borrador">Borrador</option>
                  <option value="completada">Completada</option>
                </select>
              </div>

              {/* Tipo de Servicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Servicio
                </label>
                <select
                  value={filtros.tipoServicio}
                  onChange={(e) => setFiltros({...filtros, tipoServicio: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todos los tipos</option>
                  <option value="mantenimiento_preventivo">Mantenimiento Preventivo</option>
                  <option value="correctivo">Correctivo</option>
                  <option value="reparacion">Reparación</option>
                  <option value="revision">Revisión</option>
                  <option value="instalacion">Instalación</option>
                  <option value="diagnostico">Diagnóstico</option>
                  <option value="actualizacion">Actualización</option>
                  <option value="calibracion">Calibración</option>
                  <option value="remoto">Servicio Remoto</option>
                </select>
              </div>

              {/* Técnico */}
              {user?.rol === "admin" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Técnico
                  </label>
                  <select
                    value={filtros.tecnicoId}
                    onChange={(e) => setFiltros({...filtros, tecnicoId: e.target.value})}
                    className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Todos los técnicos</option>
                    {tecnicos.map((tecnico) => {
                      const tecnicoId = tecnico._id || tecnico.id;
                      return (
                        <option key={tecnicoId} value={tecnicoId}>
                          {buildDisplayNameFromUser(tecnico) || tecnico.username}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente
                </label>
                <select
                  value={filtros.clienteId}
                  onChange={(e) => setFiltros({...filtros, clienteId: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todos los clientes</option>
                  {clientes.map((cliente) => (
                    <option key={cliente._id} value={cliente._id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sucursal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sucursal
                </label>
                <select
                  value={filtros.sucursal}
                  onChange={(e) => setFiltros({...filtros, sucursal: e.target.value})}
                  className="mobile-input w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Todas las sucursales</option>
                  {sucursales.map((sucursal) => (
                    <option key={sucursal._id} value={sucursal.nombre}>
                      {sucursal.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Botones de acción - ocupan toda la fila en móvil */}
              <div className="md:col-span-2 lg:col-span-4">
                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                  <button
                    onClick={limpiarFiltros}
                    disabled={loading}
                    className="touch-target px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:bg-gray-400"
                  >
                    Limpiar filtros
                  </button>
                  <button
                    onClick={cargarOrdenes}
                    disabled={loading}
                    className="touch-target px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:bg-emerald-400"
                  >
                    {loading ? 'Aplicando...' : 'Aplicar filtros'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista de órdenes agrupada por mes */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : filteredOrdenes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">
            {searchTerm || Object.values(filtros).some(f => f)
              ? "No se encontraron órdenes con los criterios de búsqueda."
              : "Comienza creando una nueva orden de servicio."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedByMonth.map(group => (
            <div key={group.key}>
              {/* Encabezado del mes */}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-800 capitalize">{group.label}</span>
                  <span className="ml-1 text-xs font-semibold text-white bg-emerald-500 px-2 py-0.5 rounded-full">{group.ordenes.length}</span>
                </div>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <SmartAdaptiveList
                    items={group.ordenes}
                    onItemClick={(orden) => setOrdenDetalle(orden)}
                    headers={['Fecha', 'Cliente', 'Sucursal', 'Tipo', 'Técnico', 'Estado', 'Acciones']}
                    compactHeaders={['Fecha', 'Cliente', 'Tipo', 'Estado', 'Acciones']}
                    emptyMessage={""}
                    renderDesktopRow={(orden) => (
                <>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1.5" />
                      <span>
                        {formatearFechaLista(orden.fecha || orden.fecha_programada || orden.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center max-w-[180px]" title={orden.nombre_cliente || orden.cliente_nombre}>
                      <User className="h-4 w-4 text-gray-400 mr-1.5 flex-shrink-0" />
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {orden.nombre_cliente || orden.cliente_nombre || "Sin especificar"}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center max-w-[140px]" title={orden.sucursal}>
                      <Building className="h-4 w-4 text-gray-400 mr-1.5 flex-shrink-0" />
                      <div className="text-sm text-gray-900 truncate">
                        {orden.sucursal || "Sin Sucursal"}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTipoServicioBadge(orden)}`}>
                      {getTipoServicioTexto(orden)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col max-w-[140px]">
                      <span className="truncate" title={obtenerNombreTecnico(orden)}>{obtenerNombreTecnico(orden) || "Sin asignar"}</span>
                      {orden.horas_extras_minutos > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mt-1 w-fit">
                          <Clock className="w-3 h-3 mr-1" />
                          +{formatMinutesToHHMM(orden.horas_extras_minutos)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(orden.estado)}`}>
                      {getEstadoTexto(orden.estado)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                    <div className="flex items-center space-x-1">
                      <button
                        className="inline-flex items-center justify-center p-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrdenDetalle(orden);
                        }}
                        title="Ver detalles"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {(user?.rol === "admin" || user?.rol === "tecnico") && (
                        <>
                          <button
                            className="inline-flex items-center justify-center p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditarOrden(orden);
                            }}
                            title="Editar orden"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {user?.rol === "admin" && (
                            <button
                              className="inline-flex items-center justify-center p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                eliminarOrden(orden._id);
                              }}
                              title="Eliminar orden"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </>
              )}
              renderCompactRow={(orden) => (
                <>
                  <td className="px-3 py-2 text-xs text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      {formatearFechaLista(orden.fecha || orden.fecha_programada || orden.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-medium text-gray-900 truncate max-w-[120px]" title={orden.nombre_cliente || orden.cliente_nombre}>
                      {orden.nombre_cliente || orden.cliente_nombre || "Sin especificar"}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-xs leading-4 font-medium rounded ${getTipoServicioBadge(orden)}`}>
                      {getTipoServicioTexto(orden) === 'Mantenimiento Preventivo' ? 'Preventivo' : 'Correctivo'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-xs leading-4 font-medium rounded ${getEstadoBadge(orden.estado)}`}>
                      {getEstadoTexto(orden.estado)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center space-x-1">
                      <button
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrdenDetalle(orden);
                        }}
                        title="Ver detalles"
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                      {(user?.rol === "admin" || user?.rol === "tecnico") && (
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditarOrden(orden);
                          }}
                          title="Editar orden"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </>
              )}
              renderMobileCard={(orden) => (
                <div className="space-y-3">
                  {/* Header con fecha y estado */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {formatearFechaLista(orden.fecha || orden.fecha_programada || orden.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadge(orden.estado)}`}>
                      {getEstadoTexto(orden.estado)}
                    </span>
                  </div>

                  {/* Cliente y sucursal */}
                  <div className="space-y-1">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {orden.nombre_cliente || orden.cliente_nombre || "Sin especificar"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Building className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {orden.sucursal || "Sin Sucursal"}
                      </span>
                    </div>
                  </div>

                  {/* Tipo y técnico */}
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTipoServicioBadge(orden)}`}>
                      {getTipoServicioTexto(orden)}
                    </span>
                    <div className="flex flex-col items-end">
                      <span className="text-sm text-gray-600">
                        {obtenerNombreTecnico(orden) || "Sin asignar"}
                      </span>
                      {orden.horas_extras_minutos > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          +{formatMinutesToHHMM(orden.horas_extras_minutos)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                    <button
                      className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors touch-target"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOrdenDetalle(orden);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                      Ver
                    </button>
                    {(user?.rol === "admin" || user?.rol === "tecnico") && (
                      <button
                        className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors touch-target"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditarOrden(orden);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        Editar
                      </button>
                    )}
                    {user?.rol === "admin" && (
                      <button
                        className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors touch-target"
                        onClick={(e) => {
                          e.stopPropagation();
                          eliminarOrden(orden._id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
                    />
            </div>
          ))}
        </div>
      )}

      {/* Modales */}
      <SmartModal
        isOpen={mostrarForm}
        onClose={() => {
          setMostrarForm(false);
          setEditando(null);
        }}
        title={editando ? "Editar Orden" : "Nueva Orden"}
        size="large"
      >
        <OrdenForm
          orden={editando}
          agendamientoInicial={agendamientoParaOrden}
          onClose={() => {
            setMostrarForm(false);
            setEditando(null);
            if (agendamientoParaOrden && onAgendamientoUsado) onAgendamientoUsado();
          }}
          onSaved={() => {
            setMostrarForm(false);
            setEditando(null);
            cargarOrdenes();
            if (agendamientoParaOrden && onAgendamientoUsado) onAgendamientoUsado();
          }}
        />
      </SmartModal>
      
      {ordenDetalle && (
        <OrdenDetalleModal
          orden={ordenDetalle}
          onClose={() => setOrdenDetalle(null)}
          onUpdate={cargarOrdenes}
          resolverNombreTecnico={obtenerNombreTecnico}
        />
      )}
    </div>
  );
}